// TL category showcase.
class CategoryShowcaseTabs extends HTMLElement {
  constructor() {
    super();
    this._initialised = false;
    this._observer = null;
    this.abortController = null;
    this._sectionAbortController = null;
  }

  connectedCallback() {
    if (!this._sectionAbortController) {
      this._sectionAbortController = new AbortController();
      document.addEventListener('shopify:section:load', this.onSectionLoad, {
        signal: this._sectionAbortController.signal
      });
    }

    if (this._initialised) return;

    if (!('IntersectionObserver' in window)) {
      this._init();
      return;
    }

    const target = this.closest('.tl-category-showcase') || this;
    this._observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this._observer?.disconnect();
        this._observer = null;
        this._init();
      },
      { rootMargin: '1000px 0px' }
    );
    this._observer.observe(target);
  }

  _init() {
    if (this._initialised) return;
    this._initialised = true;

    this.abortController = new AbortController();
    this.addEventListener('click', this.onClick, { signal: this.abortController.signal });
    this.addEventListener('keydown', this.onKeydown, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._observer = null;
    this._sectionAbortController?.abort();
    this._sectionAbortController = null;
    this.abortController?.abort();
    this.abortController = null;
    this._initialised = false;
  }

  onSectionLoad = (event) => {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  };

  onClick = (event) => {
    const tab = event.target.closest('[data-showcase-tab]');
    if (!tab || !this.contains(tab)) return;

    this.activateTab(tab);
  };

  onKeydown = (event) => {
    const currentTab = event.target.closest('[data-showcase-tab]');
    if (!currentTab || !this.contains(currentTab)) return;

    const tabs = this.tabs;
    const currentIndex = tabs.indexOf(currentTab);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;

    if (nextIndex === currentIndex) return;

    event.preventDefault();
    tabs[nextIndex].focus();
    this.activateTab(tabs[nextIndex]);
  };

  activateTab(activeTab) {
    const activePanelName = activeTab.dataset.showcaseTab;

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('tl-category-showcase__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    let activePanel;

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.showcasePanel === activePanelName;
      panel.classList.toggle('tl-category-showcase__panel--active', isActive);
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    });

    if (activePanel) this.hydratePanel(activePanel);

    requestAnimationFrame(() => {
      activePanel?.querySelectorAll('embla-slider').forEach((slider) => slider.reInit?.());
    });
  }

  hydratePanel(panel) {
    const template = panel.querySelector('template[data-showcase-lazy]');
    if (!template) return;
    template.replaceWith(template.content.cloneNode(true));
  }

  get tabs() {
    return Array.from(this.querySelectorAll('[data-showcase-tab]'));
  }

  get panels() {
    return Array.from(this.querySelectorAll('[data-showcase-panel]'));
  }
}

if (!customElements.get('tl-category-showcase-tabs')) {
  customElements.define('tl-category-showcase-tabs', CategoryShowcaseTabs);
}
